/**
 * 下单费用相关工具
 */
import { post, get } from '@/utils/axios.js'
import { calcPrepaymentWithCap } from '@/utils/prepayment'
import {
  PAID_BASE_METALS,
  isActivePaidMember as isActivePaidMemberByType,
  normalizeMetalCode,
  compactMetalCode,
  resolveMetalBase,
  metalCodePrefix,
  matchesMetalPrefix
} from '@/utils/orderMetal'

export {
  PAID_BASE_METALS,
  normalizeMetalCode,
  compactMetalCode,
  resolveMetalBase,
  metalCodePrefix,
  matchesMetalPrefix
}

/** 付款方式：1秒付 2货到付款 */
export const PAY_METHOD_INSTANT = 1
export const PAY_METHOD_COD = 2

/** 是否秒付 */
export function isInstantPayMethod(payMethod) {
  return Number(payMethod) !== PAY_METHOD_COD
}

/** 是否需要上传发货图片（顺丰秒付） */
export function needUploadMailImages(orderOrIndicator) {
  if (!orderOrIndicator) return false
  if (orderOrIndicator.needUploadMailImages != null) {
    return !!orderOrIndicator.needUploadMailImages
  }
  const order = orderOrIndicator.order || orderOrIndicator
  if (Number(order.transportType) !== 1) return false
  return isInstantPayMethod(order.payMethod)
}

/** 是否需要打预付款（顺丰秒付） */
export function needPrepay(orderOrIndicator) {
  if (!orderOrIndicator) return false
  if (orderOrIndicator.needPrepay != null) {
    return !!orderOrIndicator.needPrepay
  }
  const order = orderOrIndicator.order || orderOrIndicator
  if (Number(order.transportType) !== 1) return false
  return isInstantPayMethod(order.payMethod)
}

/** 付款方式展示文案 */
export function formatPayMethodText(payMethod, pricingMethod) {
  if (Number(pricingMethod) === 2) {
    return '货到付款'
  }
  const map = {
    [PAY_METHOD_INSTANT]: '秒付',
    [PAY_METHOD_COD]: '货到付款'
  }
  return map[Number(payMethod)] || '秒付'
}

/** 构建费用预览请求体 */
export function buildOrderFeePreviewPayload(options = {}) {
  const {
    items = [],
    deliveryMode = 'sf',
    feeShipOrigin = 0,
    declaredValueFeeOrigin = 0,
    selectedCouponId = '',
    memberTrialCouponId = '',
    useMemberFreeShipping = 0,
    payMethod = PAY_METHOD_INSTANT,
    forceNormalMember = false
  } = options

  const recordsList = items.map(it => ({
    metalName: it.metalName || '',
    metalNum: it.metalNum || '',
    price: Number(it.unitPrice || 0),
    weight: Number(it.weight || 0),
    purity: it.purity,
    weightImg: it.weighImageId || '',
    crossFileIds: Array.isArray(it.crossImageIds) ? it.crossImageIds : [],
    sort: Number(it.sort) || 1
  }))

  const isSf = deliveryMode === 'sf'
  return {
    recordsList,
    transportType: isSf ? 1 : 2,
    transportCost: isSf ? (feeShipOrigin || 0) : 0,
    declaredValueFee: isSf ? declaredValueFeeOrigin : 0,
    userCouponId: selectedCouponId || null,
    memberTrialCouponId: memberTrialCouponId || null,
    useMemberFreeShipping,
    payMethod: isSf ? payMethod : PAY_METHOD_COD,
    forceNormalMember: !!forceNormalMember
  }
}

/** 是否付费会员基础金属（Au/Ag/Pt/Pd） */
export function isPaidBaseMetal(metalCode) {
  return PAID_BASE_METALS.includes(resolveMetalBase(metalCode))
}

/** 是否生效中的付费会员（过期或非付费类型均视为普通会员） */
export function isActivePaidMember(memberInfo) {
  return isActivePaidMemberByType(memberInfo)
}

/** 调用后端费用预览 */
export async function fetchOrderFeePreview(payload) {
  try {
    const res = await post('/app/order/fee/preview', payload)
    if (res?.data?.code === 0) {
      return res.data.data || null
    }
    const msg = res?.data?.msg || '费用预览失败'
    console.warn('[fetchOrderFeePreview]', msg)
    return null
  } catch (error) {
    console.error('[fetchOrderFeePreview]', error)
    throw error
  }
}

/** 计算预付（带会员封顶） */
export function calcMemberPrepay(metalTotal, ratio, cap) {
  return calcPrepaymentWithCap(metalTotal, ratio, cap)
}

/** 格式化封顶金额展示 */
export function formatPrepayCap(cap) {
  const num = Number(cap) || 0
  if (num >= 10000) {
    return `${Math.round(num / 10000)}万`
  }
  return String(num)
}

/** 下单页表单选项 session 键（发货方式、付款方式） */
export const ORDER_FORM_OPTIONS_KEY = 'order_form_options'

/** 保存下单页发货/付款方式 */
export function saveOrderFormOptions(options = {}) {
  try {
    const prev = readOrderFormOptions() || {}
    const next = {
      deliveryMode: options.deliveryMode ?? prev.deliveryMode ?? 'sf',
      payMethod: options.payMethod ?? prev.payMethod ?? PAY_METHOD_INSTANT
    }
    sessionStorage.setItem(ORDER_FORM_OPTIONS_KEY, JSON.stringify(next))
  } catch (e) { /* ignore */ }
}

/** 读取下单页发货/付款方式 */
export function readOrderFormOptions() {
  try {
    const raw = sessionStorage.getItem(ORDER_FORM_OPTIONS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return {
      deliveryMode: parsed.deliveryMode === 'appointment' ? 'appointment' : 'sf',
      payMethod: Number(parsed.payMethod) === PAY_METHOD_COD ? PAY_METHOD_COD : PAY_METHOD_INSTANT
    }
  } catch (e) {
    return null
  }
}

/** 清除下单页发货/付款方式缓存 */
export function clearOrderFormOptions() {
  try {
    sessionStorage.removeItem(ORDER_FORM_OPTIONS_KEY)
  } catch (e) { /* ignore */ }
}

/** 下单页选券 session 键 */
export const ORDER_COUPON_SESSION_KEY = 'selected_order_coupon'
export const ORDER_COUPON_RETURNED_KEY = 'order_coupon_returned'
export const ORDER_COUPON_CONTEXT_KEY = 'order_coupon_context'
/** 用户在选券页主动清空后禁止自动勾选 */
export const ORDER_COUPON_USER_CLEARED_KEY = 'order_coupon_user_cleared'
/** 下单选用的会员体验券 */
export const ORDER_MEMBER_TRIAL_KEY = 'selected_order_member_trial'
/** 第一页体验卡弹窗已决策（本会话不再自动弹） */
export const ORDER_TRIAL_PROMPT_DONE_KEY = 'order_trial_prompt_done'
/** 已进入第二页：体验卡锁定，禁止取消/更换 */
export const ORDER_MEMBER_TRIAL_LOCKED_KEY = 'order_member_trial_locked'

/** 会员包邮虚拟券 ID（与后端 MemberBenefitsService.MEMBER_FREE_SHIPPING_COUPON_ID 一致） */
export const MEMBER_FREE_SHIPPING_COUPON_ID = 'member-free-shipping'

/** 读取下单页已选优惠券 */
export function readOrderSelectedCoupon() {
  try {
    const raw = sessionStorage.getItem(ORDER_COUPON_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && parsed.id ? parsed : null
  } catch (e) {
    return null
  }
}

/**
 * 保存下单页已选优惠券。
 * @param {object|null} coupon 券对象；传 null 仅清除券缓存，不会误标「从选券页返回」
 * @param {{markReturned?:boolean}} [options]
 * @param {boolean} [options.markReturned=false] 是否标记从选券页返回（仅选券页点「确定」时传 true）
 */
export function saveOrderSelectedCoupon(coupon, options = {}) {
  if (!coupon || !coupon.id) {
    sessionStorage.removeItem(ORDER_COUPON_SESSION_KEY)
  } else {
    sessionStorage.setItem(ORDER_COUPON_SESSION_KEY, JSON.stringify(coupon))
  }
  if (options.markReturned) {
    sessionStorage.setItem(ORDER_COUPON_RETURNED_KEY, '1')
  }
}

/** 保存下单会员体验券（第二页选券写入） */
export function saveOrderMemberTrial(coupon) {
  try {
    if (!coupon || !coupon.id) {
      sessionStorage.removeItem(ORDER_MEMBER_TRIAL_KEY)
      return
    }
    sessionStorage.setItem(ORDER_MEMBER_TRIAL_KEY, JSON.stringify(coupon))
  } catch (e) { /* ignore */ }
}

/** 读取下单会员体验券 */
export function readOrderMemberTrial() {
  try {
    const raw = sessionStorage.getItem(ORDER_MEMBER_TRIAL_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

/** 体验卡是否已锁定（第一页选用并进入第二页后） */
export function isOrderMemberTrialLocked() {
  try {
    return sessionStorage.getItem(ORDER_MEMBER_TRIAL_LOCKED_KEY) === '1'
  } catch (e) {
    return false
  }
}

/** 锁定体验卡（进入第二页时调用） */
export function lockOrderMemberTrial() {
  try {
    sessionStorage.setItem(ORDER_MEMBER_TRIAL_LOCKED_KEY, '1')
  } catch (e) { /* ignore */ }
}

/** 清除体验卡锁定标记 */
export function clearOrderMemberTrialLock() {
  try {
    sessionStorage.removeItem(ORDER_MEMBER_TRIAL_LOCKED_KEY)
  } catch (e) { /* ignore */ }
}

/**
 * 清除下单会员体验券。
 * @param {{force?: boolean}} [options] force 强制清除（含锁定状态）
 * @returns {boolean} 是否已清除；锁定且未 force 时返回 false
 */
export function clearOrderMemberTrial(options = {}) {
  try {
    if (!options.force && isOrderMemberTrialLocked()) {
      return false
    }
    sessionStorage.removeItem(ORDER_MEMBER_TRIAL_KEY)
    if (options.force) {
      sessionStorage.removeItem(ORDER_MEMBER_TRIAL_LOCKED_KEY)
    }
    return true
  } catch (e) {
    return false
  }
}

/** 体验卡弹窗是否已决策 */
export function isOrderTrialPromptDone() {
  try {
    return sessionStorage.getItem(ORDER_TRIAL_PROMPT_DONE_KEY) === '1'
  } catch (e) {
    return false
  }
}

/** 标记体验卡弹窗已决策 */
export function markOrderTrialPromptDone() {
  try {
    sessionStorage.setItem(ORDER_TRIAL_PROMPT_DONE_KEY, '1')
  } catch (e) { /* ignore */ }
}

/** 清除体验卡弹窗决策标记 */
export function clearOrderTrialPromptDone() {
  try {
    sessionStorage.removeItem(ORDER_TRIAL_PROMPT_DONE_KEY)
  } catch (e) { /* ignore */ }
}

/** 从第二页返回第一页时保留体验卡选用 */
export const ORDER_TRIAL_RESUME_KEY = 'order_trial_resume'

export function markOrderTrialResume() {
  try {
    sessionStorage.setItem(ORDER_TRIAL_RESUME_KEY, '1')
  } catch (e) { /* ignore */ }
}

export function consumeOrderTrialResume() {
  try {
    const hit = sessionStorage.getItem(ORDER_TRIAL_RESUME_KEY) === '1'
    sessionStorage.removeItem(ORDER_TRIAL_RESUME_KEY)
    return hit
  } catch (e) {
    return false
  }
}

/**
 * 清空本单体验卡相关 session（体验券 / 锁定 / 弹窗决策）。
 * 用于离开下单流程后重新进入，避免残留「已启用」。
 */
export function resetOrderMemberTrialSession() {
  try {
    sessionStorage.removeItem(ORDER_MEMBER_TRIAL_KEY)
    sessionStorage.removeItem(ORDER_MEMBER_TRIAL_LOCKED_KEY)
    sessionStorage.removeItem(ORDER_TRIAL_PROMPT_DONE_KEY)
    sessionStorage.removeItem(ORDER_TRIAL_RESUME_KEY)
  } catch (e) { /* ignore */ }
}

/**
 * 拉取当前用户可用会员体验券（按到期升序）。
 * @returns {Promise<Array>}
 */
export async function fetchUsableMemberTrialCoupons() {
  try {
    const res = await get('/app/coupon/member-trial/usable')
    const data = res?.data?.data
    return Array.isArray(data) ? data : []
  } catch (e) {
    console.warn('[fetchUsableMemberTrialCoupons]', e)
    return []
  }
}

/**
 * 清空下单选券相关 session（含会员包邮、选券上下文）。
 * 体验卡已锁定时保留体验券与锁定标记。
 */
export function clearOrderCouponSelection() {
  try {
    sessionStorage.removeItem(ORDER_COUPON_SESSION_KEY)
    sessionStorage.removeItem(ORDER_COUPON_RETURNED_KEY)
    sessionStorage.removeItem(ORDER_COUPON_CONTEXT_KEY)
    sessionStorage.removeItem(ORDER_COUPON_USER_CLEARED_KEY)
    sessionStorage.removeItem('selected_order_member_free_ship')
    if (!isOrderMemberTrialLocked()) {
      sessionStorage.removeItem(ORDER_MEMBER_TRIAL_KEY)
    }
  } catch (e) { /* ignore */ }
}

/**
 * 保存选券页所需的下单上下文。
 * 需携带明细 metalNum+weight，供选券页按金属分项规则校验会员包邮。
 *
 * @param {{totalWeight?:number,items?:Array,isFirstOrder?:boolean,deliveryMode?:string}} [context={}]
 */
export function saveOrderCouponContext(context = {}) {
  const items = (context.items || []).map((it) => ({
    metalNum: it?.metalNum || '',
    weight: Number(it?.weight) || 0
  }))
  sessionStorage.setItem(ORDER_COUPON_CONTEXT_KEY, JSON.stringify({
    totalWeight: Number(context.totalWeight) || 0,
    items,
    isFirstOrder: !!context.isFirstOrder,
    deliveryMode: context.deliveryMode || 'sf'
  }))
}

/** 读取选券页下单上下文 */
export function readOrderCouponContext() {
  try {
    const raw = sessionStorage.getItem(ORDER_COUPON_CONTEXT_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

/** 下单页是否刚从选券页返回 */
export function consumeOrderCouponReturn() {
  if (sessionStorage.getItem(ORDER_COUPON_RETURNED_KEY) !== '1') {
    return false
  }
  sessionStorage.removeItem(ORDER_COUPON_RETURNED_KEY)
  return true
}

/** 读取「用户主动清空选券」标记 */
export function readOrderCouponUserCleared() {
  try {
    return sessionStorage.getItem(ORDER_COUPON_USER_CLEARED_KEY) === '1'
  } catch (e) {
    return false
  }
}

/**
 * 保存「用户主动清空选券」标记。
 * 清空后禁止自动勾回包邮/最优券，直到用户再次手动选券或重置下单流程。
 */
export function saveOrderCouponUserCleared(cleared) {
  try {
    if (cleared) {
      sessionStorage.setItem(ORDER_COUPON_USER_CLEARED_KEY, '1')
    } else {
      sessionStorage.removeItem(ORDER_COUPON_USER_CLEARED_KEY)
    }
  } catch (e) { /* ignore */ }
}

/** 格式化优惠券抵扣金额 */
function formatDeductAmount(amount) {
  const n = Number(amount) || 0
  if (n <= 0) return ''
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)
}

/**
 * 优惠券选中展示（面额展示，与券面金额一致）
 * @param {object|null} coupon
 */
export function getCouponDisplayParts(coupon) {
  if (!coupon) return { label: '', amount: '' }
  if (isMemberTrialCoupon(coupon)) {
    return {
      label: coupon.couponName || coupon.memberGoodsName || '会员体验券',
      amount: ''
    }
  }
  if (isMemberFreeShippingCoupon(coupon)) {
    return {
      label: coupon.couponName || '会员包邮券',
      amount: '免邮'
    }
  }
  if (isFreeShipCoupon(coupon)) {
    const times = Math.max(Number(coupon.remainTimes) || Number(coupon.faceValue) || 1, 1)
    return {
      label: coupon.couponName || '',
      amount: `免邮×${times}`
    }
  }
  const amountStr = formatDeductAmount(coupon.faceValue)
  return {
    label: coupon.couponName || '',
    amount: amountStr ? `-¥${amountStr}` : ''
  }
}

/** 黄金、铂金、钯金包邮门槛（克）：单金属合计须达到或超过该值 */
export const FREE_SHIPPING_PRECIOUS_MIN_WEIGHT = 25

/** 白银包邮门槛（克）：白银合计须达到或超过该值 */
export const FREE_SHIPPING_SILVER_MIN_WEIGHT = 200

/** 会员包邮克重规则说明（与后端 member.free-shipping.weight-rule-desc 默认文案一致） */
export const MEMBER_FREE_SHIPPING_WEIGHT_RULE_DESC =
  '黄金、铂金、钯金每月单次寄货≥25克，或白银每月单次寄货≥200克'

/**
 * 按基础金属汇总下单克重。
 *
 * @param {Array<{metalNum?:string,weight?:number|string}>} items 下单明细
 * @returns {{Au:number,Ag:number,Pt:number,Pd:number}}
 */
export function sumWeightByMetalBase(items = []) {
  const sum = { Au: 0, Ag: 0, Pt: 0, Pd: 0 }
  ;(items || []).forEach((it) => {
    const base = resolveMetalBase(it?.metalNum)
    if (!Object.prototype.hasOwnProperty.call(sum, base)) return
    sum[base] += Number(it?.weight) || 0
  })
  return sum
}

/**
 * 是否满足会员包邮克重规则。
 * Au/Pt/Pd 任一种合计 ≥ 25g，或 Ag 合计 ≥ 200g（含边界）。
 *
 * @param {Array<{metalNum?:string,weight?:number|string}>} items 下单明细
 * @returns {boolean}
 */
export function meetsMemberFreeShippingWeight(items = []) {
  const sum = sumWeightByMetalBase(items)
  return (
    sum.Au >= FREE_SHIPPING_PRECIOUS_MIN_WEIGHT ||
    sum.Pt >= FREE_SHIPPING_PRECIOUS_MIN_WEIGHT ||
    sum.Pd >= FREE_SHIPPING_PRECIOUS_MIN_WEIGHT ||
    sum.Ag >= FREE_SHIPPING_SILVER_MIN_WEIGHT
  )
}

/**
 * 下单页优惠券行展示（现金券 / 免邮 / 会员包邮 / 体验券）。
 *
 * @param {Object|null} selectedCoupon 已选现金券/免邮券
 * @param {boolean} memberFreeShippingSelected 是否勾选会员包邮
 * @param {Object|null} [memberTrialCoupon=null] 已选会员体验券
 * @returns {{label:string,amount:string}}
 */
export function getOrderCouponRowDisplay(selectedCoupon, memberFreeShippingSelected, memberTrialCoupon = null) {
  const coupon = isMemberTrialCoupon(selectedCoupon) ? null : selectedCoupon
  const parts = []
  // 体验券与普通券同一套 label / amount 结构（体验券无 amount，不展示「会员价」）
  if (memberTrialCoupon?.id) {
    parts.push({
      label: memberTrialCoupon.couponName || memberTrialCoupon.memberGoodsName || '会员体验券',
      amount: ''
    })
  } else if (coupon && !isMemberFreeShippingCoupon(coupon)) {
    const p = getCouponDisplayParts(coupon)
    if (p.label || p.amount) parts.push(p)
  }
  if (!memberTrialCoupon?.id && (memberFreeShippingSelected || isMemberFreeShippingCoupon(coupon))) {
    parts.push({
      label: formatMemberFreeShippingSummary(true),
      amount: '免邮'
    })
  }
  if (parts.length === 0) return { label: '', amount: '' }
  if (parts.length === 1) return parts[0]
  return {
    label: parts.map(p => p.label).filter(Boolean).join(' + '),
    amount: parts.map(p => p.amount).filter(Boolean).join(' ')
  }
}

/** 优惠券展示文案（单行） */
export function formatCouponSummary(coupon) {
  if (!coupon) return ''
  const { label, amount } = getCouponDisplayParts(coupon)
  if (!amount) return label
  if (!label) return amount
  return `${label} ${amount}`
}

/**
 * 是否首单免运费（首单特惠已暂停，恒为 false）
 */
export function isFirstOrderFreightExempt() {
  // 首单特惠包邮（已暂停）
  // if (!isFreightExempt) return false
  // return freightExemptReason === 'FIRST_ORDER'
  return false
}

/** 是否展示「首单特惠」标签（首单特惠已暂停，恒为 false） */
export function showFirstOrderFreightBadge() {
  // return freightExemptReason === 'FIRST_ORDER'
  return false
}

/** 是否展示「会员包邮券」标签（含体验券本单包邮） */
export function showMemberFreeShippingFreightBadge(freightExemptReason, useMemberFreeShipping = 0) {
  if (freightExemptReason === 'FIRST_ORDER') return false
  if (freightExemptReason === 'COUPON') return false
  if (freightExemptReason === 'MEMBER' || freightExemptReason === 'MEMBER_TRIAL') return true
  return Number(useMemberFreeShipping) === 1
}

/** 是否展示「包邮券」标签（优惠券包邮次数券，非会员包邮） */
export function showCouponFreeShippingFreightBadge(freightExemptReason, selectedCoupon = null) {
  if (freightExemptReason === 'COUPON') return true
  if (freightExemptReason === 'FIRST_ORDER'
      || freightExemptReason === 'MEMBER'
      || freightExemptReason === 'MEMBER_TRIAL') return false
  return isFreeShipCoupon(selectedCoupon)
}

/** 下单页会员包邮 session 键 */
export const ORDER_MEMBER_FREE_SHIP_KEY = 'selected_order_member_free_ship'

/** 读取下单页是否勾选会员包邮 */
export function readOrderMemberFreeShipping() {
  return sessionStorage.getItem(ORDER_MEMBER_FREE_SHIP_KEY) === '1'
}

/** 保存下单页会员包邮选择 */
export function saveOrderMemberFreeShipping(selected) {
  if (selected) {
    sessionStorage.setItem(ORDER_MEMBER_FREE_SHIP_KEY, '1')
  } else {
    sessionStorage.removeItem(ORDER_MEMBER_FREE_SHIP_KEY)
  }
}

/** 券类型：1 现金抵扣券，2 免邮券，3 会员体验券 */
export const COUPON_TYPE_CASH = 1
export const COUPON_TYPE_FREE_SHIP = 2
export const COUPON_TYPE_MEMBER_TRIAL = 3

/** 现金券适用范围：1运费 2保价费 3服务费 4通用（与后端 CouponDeductUtils 一致） */
export const COUPON_SCOPE_FREIGHT = 1
export const COUPON_SCOPE_INSURANCE = 2
export const COUPON_SCOPE_SERVICE = 3
export const COUPON_SCOPE_GENERAL = 4

/** 是否为免邮券 */
export function isFreeShipCoupon(coupon) {
  return !!(coupon && Number(coupon.couponType) === COUPON_TYPE_FREE_SHIP && !isMemberFreeShippingCoupon(coupon))
}

/**
 * 未使用券是否仍在有效期内（不含会员包邮次数校验）。
 * @param {Object|null} coupon
 * @returns {boolean}
 */
export function isUnusedCouponInDate(coupon) {
  if (!coupon || !coupon.id) return false
  if (Number(coupon.status) !== 0) return false
  if (!coupon.expireTime) return true
  return new Date(coupon.expireTime).getTime() >= Date.now()
}

/**
 * 普通包邮券是否仍有可用次数。
 * @param {Object|null} coupon
 * @returns {boolean}
 */
export function hasFreeShipCouponRemain(coupon) {
  if (!isFreeShipCoupon(coupon)) return false
  const remain = Number(coupon.remainTimes)
  if (Number.isFinite(remain)) return remain > 0
  const face = Number(coupon.faceValue)
  if (Number.isFinite(face)) return face > 0
  return true
}

/**
 * 计算现金券在当前费用桶上的实际抵扣额（对齐后端 CouponDeductUtils）。
 * @param {Object|null} coupon 现金券
 * @param {{ freight?: number, insurance?: number, service?: number, freightExempt?: boolean }} [fees]
 * @returns {number}
 */
export function calcCashCouponDeduct(coupon, fees = {}) {
  if (!coupon || isFreeShipCoupon(coupon) || isMemberTrialCoupon(coupon) || isMemberFreeShippingCoupon(coupon)) {
    return 0
  }
  const face = Number(coupon.faceValue) || 0
  if (face <= 0) return 0
  const freightExempt = !!fees.freightExempt
  let freight = freightExempt ? 0 : Math.max(0, Number(fees.freight) || 0)
  let insurance = Math.max(0, Number(fees.insurance) || 0)
  let service = Math.max(0, Number(fees.service) || 0)
  const scope = Number(coupon.applicableScope) || COUPON_SCOPE_GENERAL
  let remain = face
  let total = 0
  const apply = (bucket) => {
    if (remain <= 0 || bucket <= 0) return
    const deduct = Math.round(Math.min(remain, bucket) * 100) / 100
    total += deduct
    remain = Math.round((remain - deduct) * 100) / 100
  }
  if (scope === COUPON_SCOPE_FREIGHT) apply(freight)
  else if (scope === COUPON_SCOPE_INSURANCE) apply(insurance)
  else if (scope === COUPON_SCOPE_SERVICE) apply(service)
  else {
    apply(freight)
    apply(insurance)
    apply(service)
  }
  return Math.round(total * 100) / 100
}

/**
 * 在普通券中选出最优：包邮券优先，否则取实际抵扣最高的现金券。
 * 不含会员包邮虚拟券、体验券。
 *
 * @param {Array} coupons 未使用券列表
 * @param {{ deliveryMode?: string, feeShipOrigin?: number, declaredValueFeeOrigin?: number, serviceFeeOrigin?: number, freightExempt?: boolean }} [feeContext]
 * @returns {Object|null}
 */
export function pickBestRegularCoupon(coupons, feeContext = {}) {
  const list = Array.isArray(coupons) ? coupons : []
  const deliveryMode = feeContext.deliveryMode || 'sf'
  const feeShipOrigin = Number(feeContext.feeShipOrigin) || 0
  const declaredValueFeeOrigin = Number(feeContext.declaredValueFeeOrigin) || 0
  const serviceFeeOrigin = Number(feeContext.serviceFeeOrigin) || 0
  const freightExempt = !!feeContext.freightExempt

  const expireTs = (c) => (c?.expireTime ? new Date(c.expireTime).getTime() : Number.POSITIVE_INFINITY)

  const freeShipList = list.filter((c) => isUnusedCouponInDate(c) && hasFreeShipCouponRemain(c))
  // 包邮券优先：仅顺丰且仍有运费可免时选用
  if (deliveryMode === 'sf' && !freightExempt && feeShipOrigin > 0 && freeShipList.length > 0) {
    return [...freeShipList].sort((a, b) => expireTs(a) - expireTs(b))[0] || null
  }

  const cashList = list.filter((c) => (
    isUnusedCouponInDate(c)
    && !isFreeShipCoupon(c)
    && !isMemberTrialCoupon(c)
    && !isMemberFreeShippingCoupon(c)
  ))
  let best = null
  let bestDeduct = 0
  cashList.forEach((c) => {
    const deduct = calcCashCouponDeduct(c, {
      freight: feeShipOrigin,
      insurance: declaredValueFeeOrigin,
      service: serviceFeeOrigin,
      freightExempt
    })
    if (deduct <= 0) return
    if (!best || deduct > bestDeduct) {
      best = c
      bestDeduct = deduct
      return
    }
    if (deduct === bestDeduct && expireTs(c) < expireTs(best)) {
      best = c
    }
  })
  return best
}

/** 是否为会员体验券 */
export function isMemberTrialCoupon(coupon) {
  return !!(coupon && Number(coupon.couponType) === COUPON_TYPE_MEMBER_TRIAL)
}

/** 是否为会员包邮次数虚拟券 */
export function isMemberFreeShippingCoupon(coupon) {
  return !!(coupon && (
    coupon.memberFreeShipping === true
    || coupon.id === MEMBER_FREE_SHIPPING_COUPON_ID
  ))
}

/**
 * 是否阻止使用会员包邮。
 * 现金券 / 免邮券 / 体验券与会员包邮互斥（一单只能用一种券）。
 */
export function blocksMemberFreeShipping(coupon) {
  // 任意非「会员包邮」券（含现金/免邮/体验）均与会员包邮互斥
  return !!(coupon && coupon.id && !isMemberFreeShippingCoupon(coupon))
}

/**
 * 解析本次预览/提交是否实际消耗会员包邮次数。
 * 用户已勾选但克重不足时返回 0，但不清除勾选状态。
 *
 * @param {Object} options
 * @param {boolean} [options.selected=false] 是否勾选会员包邮
 * @param {number} [options.freeShippingRemain=0] 剩余次数
 * @param {Array} [options.items=[]] 下单明细（用于金属克重校验）
 * @param {boolean|null} [options.freeShippingWeightEligible=null] 预计算是否达标；空则按 items 现场计算
 * @param {Object|null} [options.selectedCoupon=null] 已选券（与会员包邮互斥）
 * @returns {0|1} 是否提交 useMemberFreeShipping=1
 */
export function resolveUseMemberFreeShipping(options = {}) {
  const {
    selected = false,
    freeShippingRemain = 0,
    items = [],
    freeShippingWeightEligible = null,
    selectedCoupon = null
  } = options
  // 已选普通券时不走会员包邮；会员包邮虚拟券本身走 selected / id 判断
  if (blocksMemberFreeShipping(selectedCoupon)) return 0
  const useMemberShip = selected || isMemberFreeShippingCoupon(selectedCoupon)
  if (!useMemberShip) return 0
  const remain = Number(freeShippingRemain) || 0
  if (remain <= 0) return 0
  const eligible = freeShippingWeightEligible != null
    ? !!freeShippingWeightEligible
    : meetsMemberFreeShippingWeight(items)
  return eligible ? 1 : 0
}

/**
 * 会员包邮展示文案。
 *
 * @param {boolean} selected 是否已选会员包邮
 * @returns {string}
 */
export function formatMemberFreeShippingSummary(selected) {
  return selected ? '会员包邮券' : ''
}

// ---------- 下单两步草稿（第一步快照与第二页草稿隔离） ----------
/** 第一页金属快照：第二页改价/选券不得写回 */
export const ORDER_STEP1_DRAFT_KEY = 'order_step1_draft'
export const ORDER_FROM_INDEX_KEY = 'from_order_index'

/** 保存第一页快照（返回第一页只读此数据） */
export function saveOrderStep1Draft(list, orderPrice) {
  try {
    sessionStorage.setItem(ORDER_STEP1_DRAFT_KEY, JSON.stringify({ list: list || [], orderPrice: orderPrice || null }))
  } catch (e) { /* ignore */ }
}

/** 读取第一页快照 */
export function readOrderStep1Draft() {
  try {
    const raw = sessionStorage.getItem(ORDER_STEP1_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && Array.isArray(parsed.list) && parsed.list.length ? parsed : null
  } catch (e) {
    return null
  }
}

/**
 * 写入第二页可用草稿（可被第二页改价覆盖）。
 * @param {Array} list 金属明细
 * @param {string} draftId draft key
 * @param {object} [orderPrice] 盘价快照
 */
export function saveOrderStep2Draft(list, draftId, orderPrice) {
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (typeof k === 'string' && k.indexOf('order_draft_') === 0) sessionStorage.removeItem(k)
    })
    const payload = JSON.stringify(list || [])
    if (draftId) {
      sessionStorage.setItem(draftId, payload)
      sessionStorage.setItem('order_draft_latest', draftId)
    }
    sessionStorage.setItem('order_temp_list', payload)
    if (orderPrice) sessionStorage.setItem('order_price_data', JSON.stringify(orderPrice))
    sessionStorage.setItem(ORDER_FROM_INDEX_KEY, '1')
  } catch (e) { /* ignore */ }
}

/** 清除下单金属/盘价草稿 */
export function clearOrderDraftSessions() {
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (typeof k === 'string' && k.indexOf('order_draft_') === 0) sessionStorage.removeItem(k)
    })
    ;[ORDER_STEP1_DRAFT_KEY, 'order_temp_list', 'order_draft_latest', 'order_price_data', ORDER_FROM_INDEX_KEY]
      .forEach((k) => sessionStorage.removeItem(k))
  } catch (e) { /* ignore */ }
}

/**
 * 新开下单：清空两步草稿、选券、体验卡、表单选项与临时地址/银行卡。
 * 「立即卖金」入口与离开下单流程时调用，避免残留上次填写。
 */
export function resetOrderFlowSession() {
  clearOrderDraftSessions()
  clearOrderFormOptions()
  clearOrderCouponSelection()
  resetOrderMemberTrialSession()
  try {
    const keys = [
      'selected_address',
      'selected_address_override',
      'from_order_page',
      'selected_bank_card',
      'order_return_anchor',
      'order_return_scroll'
    ]
    keys.forEach((k) => sessionStorage.removeItem(k))
  } catch (e) { /* ignore */ }
}

/** 下单成功后待跳转的详情订单 ID */
export const ORDER_PENDING_DETAIL_KEY = 'order_pending_detail'

/** 下单成功：清流程缓存，并登记待跳转详情（afterEach replace 到详情以收拢历史栈） */
export function finishOrderFlow(orderId) {
  resetOrderFlowSession()
  try {
    sessionStorage.setItem('order_submit_tip', '1')
    sessionStorage.removeItem('order_detail_tip_shown')
    if (orderId) sessionStorage.setItem(ORDER_PENDING_DETAIL_KEY, String(orderId))
  } catch (e) { /* ignore */ }
}
